import { AxiosResponse } from "axios";
import { endPoints } from "utils/constants";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import { defaultMetaObj, initialContactState } from "store/constants";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const { contactEndPoint } = endPoints;

export const fetchAllContacts = createAsyncThunk(
  "contacts/fetchAllContacts",
  async (
    { page, searchParamsObj = {} }: { page: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      endpoint: contactEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteContact = createAsyncThunk(
  "contacts/deleteContact",
  async ({ contactId }: any, { rejectWithValue }) => {
    try {
      const response: any = await crudService.delete(
        contactEndPoint,
        contactId
      );

      if (response.status === 204) {
        NotificationManager.success("Contact Deleted Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const addNewContact = createAsyncThunk(
  "contacts/addNewContact",
  async (newContact: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${contactEndPoint}/`,
        newContact
      );

      if (response.status === 201) {
        NotificationManager.success("Contact Created Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const updateContact = createAsyncThunk(
  "contacts/updateContact",
  async ({ updatedContact, contactId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        contactEndPoint,
        contactId,
        updatedContact
      );
      if (response.status === 204) {
        NotificationManager.success("Contact updated Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

const contactSlice = createSlice({
  name: "contacts",
  initialState: initialContactState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all tasks
    builder
      .addCase(fetchAllContacts.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllContacts.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.contacts = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllContacts.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.meta = defaultMetaObj;
        }
      );

    // delete Task
    builder
      .addCase(deleteContact.pending, (State) => {
        State.loading = true;
      })
      .addCase(deleteContact.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
      })
      .addCase(
        deleteContact.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // add new task
    builder
      .addCase(addNewContact.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(addNewContact.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
      })
      .addCase(addNewContact.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    //   update task
    builder
      .addCase(updateContact.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateContact.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateContact.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );
  },
});

export const contactReducer = contactSlice.reducer;
